import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAppContext } from '@/composables/useAppContext';
import { useItemsGrid } from '@/composables/useItemsGrid';
import { useItemTotals } from '@/composables/useItemTotals';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useDrawingCategoryChart } from '@/charts/configs/useDrawingCategoryChart';
import { useCategoryChart } from '@/charts/configs/useCategoryChart';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const filterContext = computed(() => store.state.filterItemsContext);
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const dataLoaded = computed(() => store.getters.getDataLoaded);
  const conn = store.getters.getDBConnection;

  const drawingChart = useDrawingCategoryChart({
    chartName: 'drawingStackedBarChart',
    filterContext,
    conn
  });

  // Page-owned level ref: the category chart's IssueTemplate short-circuit
  // writes to it to skip the subitem level
  const categoryLevel = ref(0);
  const categoryChart = useCategoryChart({
    chartName: 'categoryStackedBarChart',
    filterContext,
    currentLevel: categoryLevel,
    conn
  });

  const app = useAppContext();
  const grid = useItemsGrid({ ...app, filterContext: app.filterItemsContext, t });

  // --- summary cards (D2b). This page mounts its own useItemTotals instance;
  // it is never co-mounted with ProjectPage, and nothing else registers the two
  // card filter ids, so the registrations can never collide (R11).
  const showOpenItemsOnly = app.showOpenItemsOnly;

  const {
    totalItems,
    totalSubItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    filterByMyTeams,
    filterByMe,
    setAssignedToMyTeams,
    setAssignedToMe,
    refresh: refreshTotals
  } = useItemTotals({
    conn: app.conn,
    filterContext: app.filterItemsContext,
    userId: app.userId,
    showOpenItemsOnly: app.showOpenItemsOnly
  });

  useFilterContextRefresh(app.filterItemsContext, app.dataLoaded, refreshTotals,
    { debounce: 100, immediate: true, requireDataLoaded: true });

  const handleCardFilterChange = async ({ id, selected }) => {
    if (id === 'totalAssignedToYourTeams') {
      await setAssignedToMyTeams(selected);
    } else if (id === 'totalAssignedToYou') {
      await setAssignedToMe(selected);
    }
  };

  // The full-reload cycle (R4) — same three commits, same order, as before
  const reloadWithOpenItemsOnly = (value) => {
    store.commit('setShowOpenItemsOnly', value);
    store.commit('setShowWelcomeDialog', true);
    store.commit('setLoadDashboard', true);
  };

  onMounted(async () => {
    await store.commit('resetFilters');
  });

  return {
    filterContext,
    chartFontColor,
    dataLoaded,
    drawingChart,
    categoryLevel,
    categoryChart,
    grid,
    showOpenItemsOnly,
    totalItems,
    totalSubItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    filterByMyTeams,
    filterByMe,
    handleCardFilterChange,
    reloadWithOpenItemsOnly
  };
};
